import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import type FieldObject from '../components/custom_fields/field_object';

// eslint-disable-next-line no-shadow
export enum SCORECARD_POSITIVE_RATING {
  FIFTY = '50',
  SEVENTY_FIVE = '75',
  ONE_HUNDRED = '100',
}

// eslint-disable-next-line no-shadow
export enum SCORECARD_ATTRIBUTE_MATCH {
  ANY = 'any',
  ALL = 'all',
}

// eslint-disable-next-line no-shadow
export const enum SCORECARD_STATUS {
  'ALL' = 'all',
  'COMPLETED' = 'completed',
  'INCOMPLETE' = 'incomplete',
}

export type FieldDataValue = string | Item | null;

export interface FieldData {
  customField: Item | null;
  selection?: FieldDataValue;
  currency?: Item | null;
  min?: null | string | Date;
  max?: null | string | Date;
}

export interface Settings {
  aiFeatureEnabled: boolean;
  showRegenerateButton?: boolean;
  canViewInternalCandidates?: boolean;
  showLocationFilters?: boolean;
  useProspectsSearch?: boolean;
  jobAlertsEnabled?: boolean;
  resumeFiltersEnabled?: boolean;
}

export interface Geolocation {
  latitude: string;
  longitude: string;
  location: string;
  label: string;
  value: string;
}

// eslint-disable-next-line no-shadow
export enum APPLICATION_TYPE {
  CANDIDATE = 'candidates',
  PROSPECT = 'prospects',
  ALL = 'all',
}

export interface Paths {
  appReviewPath?: string;
  cacheParamsPath?: string;
  candidateSearchPath: string;
  candidateTagsPath: string;
  countsPath: string;
  customFieldsPath: string;
  degreesPath: string;
  describeFieldOptionsPath: string;
  disciplinesPath: string;
  jobKeywordsPath: string | null;
  schoolsPath: string;
  applicationFieldsPath: string;
  rejectionReasonOptionsPath?: string;
  closePath: string;
  jobsPath?: string;
  departmentsPath?: string;
  officesPath?: string;
  prospectPoolsPath?: string;
  prospectPoolStagesPath?: string;
  prospectOwnerOptionsPath?: string;
  sourceOptionsPath?: string;
  eventOptionsPath?: string;
  rediscoveryKeywordsPath?: string;
  mapboxSuggestionsPath?: string;
  stagesPath?: string;
  savedSearchesPath?: string;
  priorityReviewPath?: string;
  rediscoveryApplicationsPath?: string;
  scorecardAttributesPath?: string;
}

type BaseFilters = {
  can_have: Array<string>;
  must_have: Array<string>;
  does_not_have: Array<string>;
  referrals_only: boolean;
  internal_only: boolean;
  candidate_tags: Item[];
  custom_fields: FieldData[];
  schools: Item[];
  degrees: Item[];
  disciplines: Item[];
  full_text: number;
  application_fields: FieldData[];
  user_date_format: string;
};

export type TalentFilteringFilters = BaseFilters & {
  hiring_plan_id: number;
  interview_stage_id: number;
  stage_status_id: number;
  scorecard_status: SCORECARD_STATUS;
  application_created_at: string | null;
  application_created_end: string | null;
  location_radius: string;
  location: Geolocation | null;
  exclude_rejected_hiring_plan_id: Array<string>;
  job_alert_subscribers: boolean;
  job_dreamers: boolean;
  years_of_experience_min: number | null;
  years_of_experience_job_name: string | null;
  use_semantic_search: boolean;
};

export type TalentRediscoveryFilters = BaseFilters & {
  milestone: Item[] | null;
  stages: Item[] | null;
  rejection_reasons: Array<string>;
  exclude_by_rejection_reasons: boolean;
  scorecard_performance: Item[] | null;
  no_strong_no_scores: boolean;
  exclude_anonymized: boolean;
  exclude_hired: boolean;
  last_activity_start: null | string;
  last_activity_end: null | string;
  jobs: Item[];
  departments: Item[];
  offices: Item[];
  job_status: 'all';
  application_type: APPLICATION_TYPE;
  prospect_pools: Item[];
  prospect_pool_stages: Item[];
  prospect_owners: Item[];
  sources: Item[];
  events: Item[];
  scorecard_attribute_filters?: Item[];
  scorecard_attribute_match?: string | null;
  page?: number;
  perPage?: number;
};

export const CandidateOnlyFilters = Object.freeze([
  'milestone',
  'scorecard_performance',
  'exclude_hired',
  'no_strong_no_scores',
  'stages',
  'scorecard_attribute_filters',
  'scorecard_attribute_match',
]);

export const ProspectOnlyFilters = Object.freeze([
  'prospect_pools',
  'prospect_pool_stages',
  'prospect_owners',
  'events',
]);

export const TalentFilteringFieldsToExclude = Object.freeze([
  'location_radius',
  'use_semantic_search',
]);

export interface SearchFormData {
  hiring_plan_id?: number;
  interview_stage_id?: number;
  job_status?: 'all';
  stage_status_id?: number;
  milestone?: Item;
  stages?: Item[];
  rejection_reasons?: Item[];
  exclude_by_rejection_reasons?: boolean;
  scorecard_performance?: Item<SCORECARD_POSITIVE_RATING> | null;
  no_strong_no_scores?: boolean;
  exclude_anonymized?: boolean;
  exclude_hired?: boolean;
  last_activity_start?: string | null;
  last_activity_end?: string | null;
  must_have: Array<string>;
  can_have: Array<string>;
  does_not_have: Array<string>;
  referrals_only: boolean;
  internal_only: boolean;
  exclude_rejected_hiring_plan_id?: Array<string>;
  candidate_tags: Item[];
  scorecard_status?: SCORECARD_STATUS;
  custom_fields: FieldObject[];
  schools: Item[];
  degrees: Item[];
  disciplines: Item[];
  full_text: 1;
  application_fields: FieldObject[];
  jobs?: Item[];
  departments?: Item[];
  offices?: Item[];
  prospect_pools?: Item[];
  prospect_pool_stages?: Item[];
  prospect_owners?: Item[];
  sources?: Item[];
  events?: Item[];
  application_created_at?: string | null;
  application_created_end?: string | null;
  location_radius?: string;
  location?: Geolocation | null;
  type?: 'all';
  application_type?: APPLICATION_TYPE;
  job_alert_subscribers: boolean;
  job_dreamers: boolean;
  years_of_experience_min?: number;
  years_of_experience_job_name?: string;
  use_semantic_search?: boolean;
  scorecard_attribute_filters?: Item[];
  scorecard_attribute_match?: string;
}

interface CustomFieldParams {
  [key: `cf_${string}`]: Array<string>;
}

export interface TalentSearchQueryParams extends CustomFieldParams {
  hiring_plan_id?: string;
  interview_stage_id?: string;
  job_status?: 'all';
  stage_status_id?: string;
  reached_milestone_type_id?: Array<string>;
  in_stages?: Array<string>;
  candidate_rejection_reason_id?: Array<string>;
  exclude_candidate_rejection_reason_id?: Array<string>;
  last_activity_start?: string;
  last_activity_end?: string;
  can_have?: Array<string>;
  must_have?: Array<string>;
  does_not_have?: Array<string>;
  search_terms?: string;
  referrals_only?: '1';
  internal_only?: '1';
  exclude_rejected_hiring_plan_id?: Array<string>;
  no_strong_no_scores?: '1';
  exclude_anonymized?: '1';
  exclude_hired?: '1';
  candidate_tag_id?: Array<string>;
  scorecard_status?: SCORECARD_STATUS;
  school_name_id?: Array<string>;
  degree_id?: Array<string>;
  discipline_id?: Array<string>;
  full_text: '1';
  use_cached_filters?: 'true' | 'false';
  department_id?: Array<string>;
  office_id?: Array<string>;
  application_created_at?: string;
  application_created_end?: string;
  scorecard_percent_yes_strong_yes?: SCORECARD_POSITIVE_RATING;
  geolocation_radius?: string;
  geolocation?: Geolocation;
  type?: APPLICATION_TYPE;
  auto_sources_to?: string;
  job_alert_subscribers?: string;
  job_dreamers?: string;
  years_of_experience_min?: string;
  years_of_experience_job_name?: string;
  use_semantic_search?: '1' | '0';
  scorecard_attribute_names?: string[];
  scorecard_attribute_match?: string;
}

export type FilterType = 'must_have' | 'can_have' | 'does_not_have';

export interface InitialFilters {
  must_have: string[];
  can_have: string[];
  does_not_have: string[];
  referrals_only: boolean;
  internal_only: boolean;
  exclude_rejected_hiring_plan_id: Array<string>;
  scorecard_status: SCORECARD_STATUS;
  candidate_tag_id: Array<Item>;
  custom_fields: FieldData[];
  schools: Array<Item>;
  degrees: Array<Item>;
  disciplines: Array<Item>;
  application_fields: FieldData[];
  application_created_at: string | null;
  application_created_end: string | null;
  geolocation_radius: string | null;
  geolocation: null | Geolocation;
  job_alert_subscribers: boolean;
  job_dreamers: boolean;
  years_of_experience_min: number | null;
  years_of_experience_job_name: string | null;
  use_semantic_search: boolean;
}

export interface Option {
  text: string;
  id: number;
  value_type?: string;
  children?: Option[];
}

export interface ItemWithOptions extends Item {
  options?: Item[];
}

export type PageName = 'rediscovery' | 'search';

export type KeywordsResponse = {
  terms: string[];
  error_message?: string;
};

export type TrackRediscoveryParams = {
  currentUserId: number;
  organizationId: number;
  filteredCount: number;
  unfilteredCount: number;
  params: SearchFormData;
};

export type TrackFilteringParams = {
  generatedKeywords: string[];
  filteredCount: number;
  unfilteredCount: number;
  params: SearchFormData;
};

export type SavedSearchParams = {
  search_params: string;
  name: string;
};

export type UpdateSavedSearchParams = {
  id: number;
  search_params: string;
  name: string;
};

export type SearchItem = {
  id: number;
  label: string;
  value: Record<string, string>;
};

export type SavedSearchItem = {
  id?: number;
  label: string;
  value?: Record<string, unknown>;
};

export type SavedSearchGroup = {
  text: string;
  items: SavedSearchItem[];
};

export type FilterOptions = {
  items?: FilterOptions[] | null | undefined;
  text?: string;
  label?: string;
  value?: Record<string, string>;
  id?: number;
};

export interface SavedSearch extends CustomFieldParams {
  [key: string]: string | number | Array<string> | null | boolean;
}
